import '@vidacamara/orange-ui/style.css';
import '@assets/styles/index.css';

import { SingleSpaProps } from '@interfaces/common.interface.ts';
import * as Sentry from '@sentry/react';
import { injectCSS, removeCSS } from '@utils/common/single-spa-styles.util.ts';
import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import singleSpaReact from 'single-spa-react';
import { cssLifecycleFactory } from 'vite-plugin-single-spa/ex';

import { version } from '../package.json';
import App from './main.tsx';

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
	tracesSampleRate: 1.0,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	environment: import.meta.env.MODE,
	enabled: import.meta.env.MODE === 'qa' || import.meta.env.MODE === 'prod',
});

const lc = singleSpaReact({
	React,
	ReactDOMClient,
	loadRootComponent: async () => {
		const { default: App } = await import('./main');
		return App;
	},
	errorBoundary(err) {
		return <div>Error: {err.message}</div>;
	},
});

let cssLink: HTMLLinkElement;

const customCssLifecycle = {
	bootstrap: lc.bootstrap,
	mount: async (props: SingleSpaProps) => {
		const stylesURL = import.meta.env.VITE_MF_STYLES_URL;
		const [base] = stylesURL.split('.css');
		const newStylesURL = `${base}_${version}.css`;
		cssLink = await injectCSS(newStylesURL);

		return lc.mount({
			...props,
			eventEmitter: props.eventEmitter,
		});
	},
	unmount: (props: SingleSpaProps) => {
		removeCSS(cssLink);
		return lc.unmount(props);
	},
};

const { bootstrap: cssHMRBootstrap, mount: cssHMRMount, unmount: cssHMRUnmount } = cssLifecycleFactory('root-config');

const cssHMRLc = {
	bootstrap: [cssHMRBootstrap, lc.bootstrap],
	mount: [cssHMRMount, lc.mount],
	unmount: [cssHMRUnmount, lc.unmount],
};

const cssLcByEnv = import.meta.env.MODE === 'local-dev' ? cssHMRLc : customCssLifecycle;

export const bootstrap = cssLcByEnv.bootstrap;
export const mount = cssLcByEnv.mount;
export const unmount = cssLcByEnv.unmount;

if (!window.__SINGLE_SPA_SIDE__) {
	const container = document.getElementById('root');
	if (container) {
		const root = ReactDOMClient.createRoot(container);
		root.render(<App />);
	} else {
		document.body.innerHTML += `<div data-testid="error-loaded"><h2>Loaded error...</h2></div>`;
	}
}
